<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { Icon } from '@iconify/vue';
import axios from 'axios';
import SelectSearch from '@/Components/SelectSearch.vue';

const props = defineProps({
    courses: Array,
    students: Array,
    bankAccounts: Array,
    anioEscolar: Number,
    permissions: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['order-paid', 'open-payment-modal']);

// Opciones para SelectSearch
const studentOptions = computed(() => {
    return (props.students || []).map(s => ({
        value: s.id,
        label: `${s.apellidos} ${s.nombres} ${s.num_doc ? '(' + s.num_doc + ')' : ''}`
    }));
});

const courseMassiveOptions = computed(() => {
    return [
        { value: '', label: 'Todos los cursos de formación' },
        ...(props.courses || []).map(c => ({
            value: c.id,
            label: c.label + (c.price ? ` (S/ ${Number(c.price).toFixed(2)})` : '')
        }))
    ];
});

const courseIndividualOptions = computed(() => {
    let availableCourses = props.courses || [];

    // Si hay un estudiante seleccionado, filtrar solo los cursos en los que está matriculado
    if (orderForm.value.id_student) {
        const student = (props.students || []).find(s => String(s.id) === String(orderForm.value.id_student));
        if (student && student.enrolled_courses && Array.isArray(student.enrolled_courses)) {
            const enrolledIds = student.enrolled_courses.map(id => String(id));
            availableCourses = availableCourses.filter(c => enrolledIds.includes(String(c.id)));
        }
    }

    return [
        { value: '', label: 'Sin curso específico (General)' },
        ...availableCourses.map(c => ({
            value: c.id,
            label: c.label + (c.price ? ` (S/ ${Number(c.price).toFixed(2)})` : '')
        }))
    ];
});

// Estado de la tabla
const orders = ref([]);
const pagination = ref({ current_page: 1, last_page: 1, total: 0 });
const loading = ref(false);

// Selección múltiple para acciones masivas
const selectedOrderIds = ref([]);
const isDeletingMassive = ref(false);

const isAllSelected = computed(() => {
    if (!orders.value || orders.value.length === 0) return false;
    return orders.value.every(o => selectedOrderIds.value.includes(o.id));
});

const toggleSelectAll = () => {
    if (isAllSelected.value) {
        selectedOrderIds.value = [];
    } else {
        selectedOrderIds.value = orders.value.map(o => o.id);
    }
};

const toggleSelectOrder = (id) => {
    const idx = selectedOrderIds.value.indexOf(id);
    if (idx > -1) {
        selectedOrderIds.value.splice(idx, 1);
    } else {
        selectedOrderIds.value.push(id);
    }
};

// Filtros
const filters = ref({
    id_course: '',
    id_student: '',
    periodo: '',
    status: 'all',
    search: '',
    page: 1,
    per_page: 15
});

// Cargar órdenes
const fetchOrders = async (page = 1) => {
    if (props.permissions?.orders_read === false) return;
    loading.value = true;
    filters.value.page = page;
    try {
        const response = await axios.get(route('financiera-orders.list'), {
            params: filters.value
        });
        orders.value = response.data.data;
        pagination.value = {
            current_page: response.data.current_page,
            last_page: response.data.last_page,
            total: response.data.total
        };
    } catch (error) {
        if (error.response?.status !== 403) {
            console.error('Error al cargar órdenes:', error);
        }
    } finally {
        loading.value = false;
    }
};

let searchTimeout = null;
const onSearchInput = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        fetchOrders(1);
    }, 400);
};

// Exportar órdenes
const exportExcel = () => {
    const params = new URLSearchParams(filters.value).toString();
    window.location.href = route('financiera-orders.export-excel') + '?' + params;
};

const exportPdf = () => {
    const params = new URLSearchParams(filters.value).toString();
    window.open(route('financiera-orders.export-pdf') + '?' + params, '_blank');
};

// Modal de Programación Masiva
const showMassiveModal = ref(false);
const massiveForm = ref({
    id_course: '',
    periodo: '',
    label: '',
    total_amount: 150.00,
    fecha_emision: new Date().toISOString().substring(0, 10),
    fecha_vencimiento: new Date(Date.now() + 15 * 86400000).toISOString().substring(0, 10),
    note: ''
});
const savingMassive = ref(false);
const massiveResult = ref(null);

const selectedMassiveCourse = computed(() => {
    if (!massiveForm.value.id_course) return null;
    return (props.courses || []).find(c => String(c.id) === String(massiveForm.value.id_course));
});

const massiveCoursePeriods = computed(() => {
    return selectedMassiveCourse.value?.periods || [];
});

const applyMassivePeriod = (p) => {
    massiveForm.value.periodo = p.periodo;
    massiveForm.value.label = p.label;
    if (p.start_date) massiveForm.value.fecha_emision = p.start_date;
    if (p.end_date) massiveForm.value.fecha_vencimiento = p.end_date;
    if (selectedMassiveCourse.value?.price) {
        massiveForm.value.total_amount = Number(selectedMassiveCourse.value.price);
    }
};

watch(() => massiveForm.value.id_course, (newVal) => {
    if (newVal) {
        const course = (props.courses || []).find(c => String(c.id) === String(newVal));
        if (course) {
            if (course.price) massiveForm.value.total_amount = Number(course.price);
            if (course.periods && course.periods.length > 0) {
                applyMassivePeriod(course.periods[0]);
            }
        }
    }
});

const openMassiveModal = () => {
    massiveForm.value = {
        id_course: '',
        source_mode: 'configured',
        periodo: `${props.anioEscolar || new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}`,
        label: `Mensualidad Cursos de Formación - ${new Date().toLocaleString('es-ES', { month: 'long' })}`,
        total_amount: 150.00,
        fecha_emision: new Date().toISOString().substring(0, 10),
        fecha_vencimiento: new Date(Date.now() + 15 * 86400000).toISOString().substring(0, 10),
        note: ''
    };
    massiveResult.value = null;
    showMassiveModal.value = true;
};

const submitMassive = async () => {
    savingMassive.value = true;
    massiveResult.value = null;
    try {
        const res = await axios.post(route('financiera-orders.generate-massive'), massiveForm.value);
        massiveResult.value = { success: true, message: res.data.message };
        fetchOrders(1);
        setTimeout(() => {
            if (massiveResult.value?.success) showMassiveModal.value = false;
        }, 1800);
    } catch (err) {
        massiveResult.value = {
            success: false,
            message: err.response?.data?.message || 'Error al generar órdenes masivas.'
        };
    } finally {
        savingMassive.value = false;
    }
};

// Modal de Creación / Edición Individual
const showOrderModal = ref(false);
const isEditing = ref(false);
const orderForm = ref({
    id: null,
    id_student: '',
    id_course: '',
    periodo: '',
    label: '',
    total_amount: 150.00,
    fecha_emision: '',
    fecha_vencimiento: '',
    status: 0,
    note: ''
});
const savingOrder = ref(false);
const orderError = ref('');

const selectedIndividualCourse = computed(() => {
    if (!orderForm.value.id_course) return null;
    return (props.courses || []).find(c => String(c.id) === String(orderForm.value.id_course));
});

const individualCoursePeriods = computed(() => {
    return selectedIndividualCourse.value?.periods || [];
});

const applyIndividualPeriod = (p) => {
    orderForm.value.periodo = p.periodo;
    orderForm.value.label = p.label;
    if (p.start_date) orderForm.value.fecha_emision = p.start_date;
    if (p.end_date) orderForm.value.fecha_vencimiento = p.end_date;
    if (selectedIndividualCourse.value?.price) {
        orderForm.value.total_amount = Number(selectedIndividualCourse.value.price);
    }
};

// Watcher para filtrar y autoseleccionar cursos matriculados al cambiar de estudiante
watch(() => orderForm.value.id_student, (newStudentId) => {
    if (newStudentId && !isEditing.value) {
        const student = (props.students || []).find(s => String(s.id) === String(newStudentId));
        if (student && student.enrolled_courses && student.enrolled_courses.length > 0) {
            const enrolledIds = student.enrolled_courses.map(id => String(id));
            if (!enrolledIds.includes(String(orderForm.value.id_course))) {
                orderForm.value.id_course = student.enrolled_courses[0];
            }
        } else {
            orderForm.value.id_course = '';
        }
    }
});

const openCreateModal = () => {
    isEditing.value = false;
    orderError.value = '';
    orderForm.value = {
        id: null,
        id_student: '',
        id_course: '',
        periodo: `${props.anioEscolar || new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}`,
        label: 'Matrícula / Mensualidad Curso',
        total_amount: 150.00,
        fecha_emision: new Date().toISOString().substring(0, 10),
        fecha_vencimiento: new Date(Date.now() + 15 * 86400000).toISOString().substring(0, 10),
        status: 0,
        note: ''
    };
    showOrderModal.value = true;
};

const openEditModal = (order) => {
    isEditing.value = true;
    orderError.value = '';
    orderForm.value = {
        id: order.id,
        id_student: order.id_student,
        id_course: order.id_course,
        periodo: order.periodo,
        label: order.label,
        total_amount: Number(order.total_amount),
        fecha_emision: order.fecha_emision,
        fecha_vencimiento: order.fecha_vencimiento,
        status: order.status,
        note: order.note || ''
    };
    showOrderModal.value = true;
};

const submitOrder = async () => {
    savingOrder.value = true;
    orderError.value = '';
    try {
        if (isEditing.value) {
            await axios.put(route('financiera-orders.update', orderForm.value.id), orderForm.value);
        } else {
            await axios.post(route('financiera-orders.store'), orderForm.value);
        }
        showOrderModal.value = false;
        fetchOrders(pagination.value.current_page);
    } catch (err) {
        orderError.value = err.response?.data?.message || 'Error al guardar la orden.';
    } finally {
        savingOrder.value = false;
    }
};

// Eliminar Orden Individual
const deleteOrder = async (order) => {
    if (!confirm(`¿Está seguro de eliminar la orden ${order.ref}?`)) return;
    try {
        const res = await axios.delete(route('financiera-orders.destroy', order.id));
        if (res.data.success) {
            fetchOrders(pagination.value.current_page);
        } else {
            alert(res.data.message);
        }
    } catch (err) {
        alert(err.response?.data?.message || 'Error al eliminar la orden.');
    }
};

// Eliminar Órdenes Masivamente
const deleteMassiveOrders = async (allFiltered = false) => {
    const count = allFiltered ? pagination.value.total : selectedOrderIds.value.length;
    if (count === 0) return;

    const message = allFiltered
        ? `¿Está seguro de eliminar TODAS las ${count} órdenes filtradas que NO tengan pagos registrados?`
        : `¿Está seguro de eliminar las ${count} órdenes seleccionadas que NO tengan pagos registrados?`;

    if (!confirm(message)) return;

    isDeletingMassive.value = true;
    try {
        const res = await axios.post(route('financiera-orders.destroy-massive'), {
            order_ids: selectedOrderIds.value,
            all_filtered: allFiltered,
            ...filters.value
        });
        alert(res.data.message);
        selectedOrderIds.value = [];
        fetchOrders(pagination.value.current_page);
    } catch (err) {
        alert(err.response?.data?.message || 'Error al eliminar órdenes masivamente.');
    } finally {
        isDeletingMassive.value = false;
    }
};

// Modal de Edición Masiva de Órdenes (Precio, Fecha Emisión, Fecha Vencimiento)
const showMassiveEditModal = ref(false);
const massiveEditForm = ref({
    all_filtered: false,
    change_amount: false,
    total_amount: 150.00,
    change_emission_date: false,
    fecha_emision: '',
    change_due_date: false,
    fecha_vencimiento: ''
});
const isUpdatingMassive = ref(false);
const massiveEditResult = ref(null);

const openMassiveEditModal = (allFiltered = false) => {
    massiveEditForm.value = {
        all_filtered: allFiltered,
        change_amount: false,
        total_amount: 150.00,
        change_emission_date: false,
        fecha_emision: '',
        change_due_date: false,
        fecha_vencimiento: ''
    };
    massiveEditResult.value = null;
    showMassiveEditModal.value = true;
};

const submitMassiveEdit = async () => {
    if (!massiveEditForm.value.change_amount && !massiveEditForm.value.change_emission_date && !massiveEditForm.value.change_due_date) {
        alert('Seleccione al menos un campo a modificar (precio, fecha de emisión o fecha de vencimiento).');
        return;
    }

    isUpdatingMassive.value = true;
    massiveEditResult.value = null;
    try {
        const res = await axios.post(route('financiera-orders.update-massive'), {
            order_ids: selectedOrderIds.value,
            ...massiveEditForm.value,
            ...filters.value
        });
        massiveEditResult.value = { success: true, message: res.data.message };
        selectedOrderIds.value = [];
        fetchOrders(pagination.value.current_page);
        setTimeout(() => {
            if (massiveEditResult.value?.success) showMassiveEditModal.value = false;
        }, 1800);
    } catch (err) {
        massiveEditResult.value = {
            success: false,
            message: err.response?.data?.message || 'Error al actualizar órdenes masivamente.'
        };
    } finally {
        isUpdatingMassive.value = false;
    }
};

// Iniciar pago de una orden
const payOrder = (order) => {
    emit('open-payment-modal', {
        id_student: order.id_student,
        preselected_order: order
    });
};

onMounted(() => {
    fetchOrders(1);
});
</script>

<template>
    <div class="space-y-6">
        <!-- Barra de Acciones y Filtros -->
        <div class="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 shadow-sm space-y-4">
            <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div class="flex flex-wrap items-center gap-2">
                    <button v-if="permissions?.orders_write !== false" @click="openMassiveModal" class="btn-premium-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-md transition-all">
                        <Icon icon="tabler:calendar-plus" class="w-5 h-5" />
                        <span>Programar Órdenes (Masivo)</span>
                    </button>
                    <button v-if="permissions?.orders_write !== false" @click="openCreateModal" class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-all">
                        <Icon icon="tabler:plus" class="w-5 h-5" />
                        <span>Nueva Orden</span>
                    </button>

                    <!-- Botones de Exportación -->
                    <div class="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-1 hidden sm:block"></div>
                    <button @click="exportExcel" class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 transition-all" title="Exportar órdenes filtradas a Excel">
                        <Icon icon="tabler:file-spreadsheet" class="w-4 h-4 text-emerald-600" />
                        <span>Excel</span>
                    </button>
                    <button @click="exportPdf" class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 transition-all" title="Exportar órdenes filtradas a PDF">
                        <Icon icon="tabler:file-type-pdf" class="w-4 h-4 text-rose-600" />
                        <span>PDF</span>
                    </button>
                </div>

                <div class="flex items-center gap-3">
                    <span class="text-xs font-semibold text-slate-400">Total: {{ pagination.total }} órdenes</span>
                </div>
            </div>

            <!-- Barra flotante de acciones masivas cuando hay seleccionados -->
            <div v-if="selectedOrderIds.length > 0" class="p-3 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 rounded-xl flex flex-wrap items-center justify-between gap-3 animate-in fade-in duration-150">
                <div class="flex items-center gap-2 text-xs font-bold text-indigo-900 dark:text-indigo-200">
                    <Icon icon="tabler:checkbox" class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span>{{ selectedOrderIds.length }} orden(es) seleccionada(s)</span>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                    <!-- Botón Modificar / Editar Masivo -->
                    <button v-if="permissions?.orders_write !== false" @click="openMassiveEditModal(false)" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all shadow-xs">
                        <Icon icon="tabler:edit" class="w-3.5 h-3.5" />
                        <span>Modificar Precio/Fechas</span>
                    </button>
                    <!-- Botón Eliminar Seleccionadas -->
                    <button v-if="permissions?.orders_write !== false" @click="deleteMassiveOrders(false)" :disabled="isDeletingMassive" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-lg transition-all shadow-xs disabled:opacity-50">
                        <Icon icon="tabler:trash" class="w-3.5 h-3.5" />
                        <span>Eliminar Seleccionadas</span>
                    </button>
                    <!-- Botón Eliminar Todas las Filtradas -->
                    <button v-if="permissions?.orders_write !== false" @click="deleteMassiveOrders(true)" :disabled="isDeletingMassive" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-rose-100 hover:bg-rose-200 dark:bg-rose-950 text-rose-700 dark:text-rose-300 rounded-lg transition-all border border-rose-300 disabled:opacity-50">
                        <Icon icon="tabler:trash-x" class="w-3.5 h-3.5" />
                        <span>Eliminar Todas las Filtradas ({{ pagination.total }})</span>
                    </button>
                    <button @click="selectedOrderIds = []" class="px-2.5 py-1.5 text-xs font-semibold bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-100">
                        Deseleccionar
                    </button>
                </div>
            </div>

            <!-- Filtros secundarios -->
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                <!-- Buscador -->
                <div class="relative">
                    <Icon icon="tabler:search" class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input type="text" v-model="filters.search" @input="onSearchInput" placeholder="Buscar ref, alumno, DNI..." class="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500/20" />
                </div>

                <!-- Filtro por Curso -->
                <div>
                    <select v-model="filters.id_course" @change="fetchOrders(1)" class="w-full py-2 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
                        <option value="">Todos los cursos</option>
                        <option v-for="c in courses" :key="c.id" :value="c.id">{{ c.label }}</option>
                    </select>
                </div>

                <!-- Filtro por Estado -->
                <div>
                    <select v-model="filters.status" @change="fetchOrders(1)" class="w-full py-2 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
                        <option value="all">Todos los estados</option>
                        <option value="0">Pendientes</option>
                        <option value="2">Pago Parcial</option>
                        <option value="1">Pagados</option>
                        <option value="3">Anulados</option>
                    </select>
                </div>

                <!-- Filtro por Periodo -->
                <div>
                    <input type="text" v-model="filters.periodo" @change="fetchOrders(1)" placeholder="Periodo (ej. 202608)" class="w-full py-2 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                </div>

                <!-- Botón Reset -->
                <div class="flex items-center">
                    <button @click="filters = { id_course: '', id_student: '', periodo: '', status: 'all', search: '', page: 1, per_page: 15 }; fetchOrders(1);" class="w-full py-2 px-3 text-xs font-semibold rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-1.5">
                        <Icon icon="tabler:refresh" class="w-4 h-4" />
                        <span>Limpiar</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Tabla de Órdenes de Pago -->
        <div class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">
            <div v-if="loading" class="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
                <Icon icon="tabler:loader-2" class="w-8 h-8 animate-spin text-indigo-500" />
                <span class="text-sm font-medium">Cargando órdenes de pago...</span>
            </div>

            <div v-else-if="orders.length === 0" class="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
                <Icon icon="tabler:file-invoice" class="w-12 h-12 text-slate-300 dark:text-slate-600" />
                <p class="text-base font-semibold text-slate-600 dark:text-slate-300">No se encontraron órdenes de pago</p>
                <p class="text-xs text-slate-400 max-w-sm">Utilice el botón "Programar Órdenes (Masivo)" para generar los cobros a los estudiantes matriculados.</p>
            </div>

            <div v-else class="overflow-x-auto">
                <table class="w-full text-left text-xs">
                    <thead class="bg-slate-50 dark:bg-slate-900/60 text-slate-500 font-semibold uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">
                        <tr>
                            <th class="py-3.5 px-3 w-10 text-center">
                                <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll" class="rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 dark:border-slate-700" title="Seleccionar todas de la página" />
                            </th>
                            <th class="py-3.5 px-4">Ref / Periodo</th>
                            <th class="py-3.5 px-4">Estudiante</th>
                            <th class="py-3.5 px-4">Curso / Concepto</th>
                            <th class="py-3.5 px-4">Vencimiento</th>
                            <th class="py-3.5 px-4 text-right">Total</th>
                            <th class="py-3.5 px-4 text-right">Abonado</th>
                            <th class="py-3.5 px-4 text-right">Saldo</th>
                            <th class="py-3.5 px-4 text-center">Estado</th>
                            <th class="py-3.5 px-4 text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 dark:divide-slate-700/60 font-medium">
                        <tr v-for="order in orders" :key="order.id" :class="selectedOrderIds.includes(order.id) ? 'bg-indigo-50/40 dark:bg-indigo-950/20' : 'hover:bg-slate-50/60 dark:hover:bg-slate-750'" class="transition-colors">
                            <!-- Checkbox -->
                            <td class="py-3 px-3 text-center">
                                <input type="checkbox" :checked="selectedOrderIds.includes(order.id)" @change="toggleSelectOrder(order.id)" class="rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 dark:border-slate-700" />
                            </td>

                            <!-- Ref y Periodo -->
                            <td class="py-3 px-4">
                                <div class="font-bold text-slate-800 dark:text-white">{{ order.ref }}</div>
                                <div class="text-[11px] text-slate-400">Periodo: {{ order.periodo }}</div>
                            </td>

                            <!-- Estudiante -->
                            <td class="py-3 px-4">
                                <div class="font-semibold text-slate-800 dark:text-slate-200">
                                    {{ order.student?.person?.apellidos }} {{ order.student?.person?.nombres }}
                                </div>
                                <div class="text-[11px] text-slate-400">DNI: {{ order.student?.person?.num_doc || 'S/N' }}</div>
                            </td>

                            <!-- Curso y Concepto -->
                            <td class="py-3 px-4">
                                <div class="text-indigo-600 dark:text-indigo-400 font-semibold">{{ order.course?.label || 'General' }}</div>
                                <div class="text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-xs">{{ order.label }}</div>
                            </td>

                            <!-- Vencimiento -->
                            <td class="py-3 px-4">
                                <span class="text-slate-700 dark:text-slate-300">{{ order.fecha_vencimiento }}</span>
                            </td>

                            <!-- Montos -->
                            <td class="py-3 px-4 text-right font-bold text-slate-800 dark:text-white">
                                S/ {{ Number(order.total_amount).toFixed(2) }}
                            </td>
                            <td class="py-3 px-4 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                                S/ {{ Number(order.paid_amount).toFixed(2) }}
                            </td>
                            <td class="py-3 px-4 text-right font-bold" :class="Number(order.balance) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'">
                                S/ {{ Number(order.balance).toFixed(2) }}
                            </td>

                            <!-- Estado Badge -->
                            <td class="py-3 px-4 text-center">
                                <span v-if="order.status == 1" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                                    <Icon icon="tabler:circle-check" class="w-3.5 h-3.5" />
                                    Pagado
                                </span>
                                <span v-else-if="order.status == 2" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300">
                                    <Icon icon="tabler:clock-hour-4" class="w-3.5 h-3.5" />
                                    Pago Parcial
                                </span>
                                <span v-else-if="order.status == 3" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                    <Icon icon="tabler:ban" class="w-3.5 h-3.5" />
                                    Anulado
                                </span>
                                <span v-else class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
                                    <Icon icon="tabler:alert-circle" class="w-3.5 h-3.5" />
                                    Pendiente
                                </span>
                            </td>

                            <!-- Acciones -->
                            <td class="py-3 px-4 text-center">
                                <div class="flex items-center justify-center gap-1.5">
                                    <!-- Botón Pagar / Abonar si no está pagado total -->
                                    <button v-if="order.status !== 1 && permissions?.payments_write !== false" @click="payOrder(order)" title="Abonar Pago" class="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/60 dark:text-emerald-300 transition-colors">
                                        <Icon icon="tabler:cash" class="w-4 h-4" />
                                    </button>

                                    <!-- Editar -->
                                    <button v-if="permissions?.orders_write !== false" @click="openEditModal(order)" title="Editar Orden" class="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-300 transition-colors">
                                        <Icon icon="tabler:pencil" class="w-4 h-4" />
                                    </button>

                                    <!-- Eliminar si no tiene abonos -->
                                    <button v-if="Number(order.paid_amount) === 0 && permissions?.orders_write !== false" @click="deleteOrder(order)" title="Eliminar Orden" class="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:hover:bg-rose-900/60 dark:text-rose-400 transition-colors">
                                        <Icon icon="tabler:trash" class="w-4 h-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Paginación -->
            <div v-if="pagination.last_page > 1" class="px-5 py-3.5 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <span class="text-xs text-slate-500">Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
                <div class="flex items-center gap-1.5">
                    <button :disabled="pagination.current_page <= 1" @click="fetchOrders(pagination.current_page - 1)" class="px-3 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-white dark:hover:bg-slate-800">Anterior</button>
                    <button :disabled="pagination.current_page >= pagination.last_page" @click="fetchOrders(pagination.current_page + 1)" class="px-3 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-white dark:hover:bg-slate-800">Siguiente</button>
                </div>
            </div>
        </div>

        <!-- Modal Programación Masiva -->
        <div v-if="showMassiveModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div class="bg-white dark:bg-slate-800 rounded-2xl max-w-xl w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-5 animate-in fade-in zoom-in-95 duration-200">
                <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
                    <div class="flex items-center gap-2.5">
                        <div class="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">
                            <Icon icon="tabler:calendar-event" class="w-6 h-6" />
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-slate-800 dark:text-white">Programación Masiva de Órdenes</h3>
                            <p class="text-xs text-slate-400">Genera cobros a todos los alumnos matriculados en cursos de formación</p>
                        </div>
                    </div>
                    <button @click="showMassiveModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1">
                        <Icon icon="tabler:x" class="w-5 h-5" />
                    </button>
                </div>

                <div v-if="massiveResult" class="p-3.5 rounded-xl text-xs font-semibold" :class="massiveResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'">
                    {{ massiveResult.message }}
                </div>

                <form @submit.prevent="submitMassive" class="space-y-4 text-xs">
                    <!-- Curso -->
                    <div>
                        <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Curso de Formación</label>
                        <SelectSearch
                            v-model="massiveForm.id_course"
                            :options="courseMassiveOptions"
                            placeholder="Buscar y seleccionar curso..."
                        />
                    </div>

                    <!-- Selector de Modo de Generación cuando se seleccionan Todos los Cursos -->
                    <div v-if="!massiveForm.id_course || massiveForm.id_course === '' || massiveForm.id_course === 'all'" class="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 space-y-2.5">
                        <label class="block font-bold text-slate-800 dark:text-slate-200 text-xs">
                            ¿Cómo deseas completar los datos de las órdenes?
                        </label>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            <!-- Opción 1: Datos previamente configurados por cada curso -->
                            <div @click="massiveForm.source_mode = 'configured'"
                                 :class="massiveForm.source_mode === 'configured' ? 'border-indigo-600 bg-indigo-50/70 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-200 ring-2 ring-indigo-500/20' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'"
                                 class="p-3 rounded-xl border cursor-pointer transition-all flex items-start gap-2.5">
                                <input type="radio" value="configured" v-model="massiveForm.source_mode" class="mt-0.5 text-indigo-600 focus:ring-indigo-500" />
                                <div>
                                    <div class="font-bold text-xs">Con datos configurados de cada curso</div>
                                    <div class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                                        Toma el precio y periodo configurado en cada curso. <span class="font-semibold text-slate-700 dark:text-slate-200">Si un curso no tiene configuración, no se crearán órdenes para ese curso.</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Opción 2: Usar este contenido para todos -->
                            <div @click="massiveForm.source_mode = 'custom'"
                                 :class="massiveForm.source_mode === 'custom' ? 'border-indigo-600 bg-indigo-50/70 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-200 ring-2 ring-indigo-500/20' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'"
                                 class="p-3 rounded-xl border cursor-pointer transition-all flex items-start gap-2.5">
                                <input type="radio" value="custom" v-model="massiveForm.source_mode" class="mt-0.5 text-indigo-600 focus:ring-indigo-500" />
                                <div>
                                    <div class="font-bold text-xs">Usar este contenido para todos</div>
                                    <div class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                                        Aplica el mismo periodo, monto, concepto y fechas ingresadas abajo a todos los cursos por igual.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Banner informativo para Modo Configurado con todos los cursos -->
                    <div v-if="(!massiveForm.id_course || massiveForm.id_course === '' || massiveForm.id_course === 'all') && massiveForm.source_mode === 'configured'"
                         class="p-4 rounded-xl bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-200/80 dark:border-indigo-900/60 space-y-2 animate-in fade-in duration-150">
                        <div class="flex items-center gap-2 text-indigo-900 dark:text-indigo-200 font-bold text-xs">
                            <Icon icon="tabler:info-circle" class="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
                            <span>Generación automática por configuración de cada curso</span>
                        </div>
                        <p class="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                            Se generará la orden a cada estudiante matriculado utilizando el <strong>precio establecido</strong> y las <strong>fechas del periodo configurado</strong> en su curso. Los cursos sin configuración previa de precio o periodos <strong>no generarán órdenes</strong>.
                        </p>
                        <div class="pt-2 border-t border-indigo-100 dark:border-indigo-900/50">
                            <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Periodo a generar (opcional, ej. 202601 - dejar vacío para tomar el periodo según corresponda):</label>
                            <input type="text" v-model="massiveForm.periodo" placeholder="Ej. 202601 (opcional)" class="w-full py-1.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 font-mono text-xs" />
                        </div>
                    </div>

                    <!-- Campos del formulario manual (solo en modo Custom o cuando se selecciona un curso específico) -->
                    <div v-else class="space-y-4 animate-in fade-in duration-150">
                        <!-- Periodos configurados en el curso (si existen para un curso específico) -->
                        <div v-if="massiveCoursePeriods && massiveCoursePeriods.length > 0" class="p-3.5 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/50 space-y-2">
                            <div class="flex items-center justify-between">
                                <span class="font-bold text-indigo-900 dark:text-indigo-200 text-[11px] flex items-center gap-1.5">
                                    <Icon icon="tabler:calendar-month" class="w-3.5 h-3.5 text-indigo-600" />
                                    Periodos Configurados en este Curso:
                                </span>
                                <span class="text-[10px] text-slate-400">Haz clic para auto-completar</span>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <button v-for="p in massiveCoursePeriods" :key="p.id" type="button" @click="applyMassivePeriod(p)"
                                        :class="massiveForm.periodo === p.periodo ? 'bg-indigo-600 text-white font-bold shadow-xs' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-indigo-400'"
                                        class="p-2 rounded-lg text-left text-xs transition-all flex flex-col justify-between">
                                    <div class="font-semibold truncate">{{ p.label }}</div>
                                    <div class="text-[10px] opacity-80 flex items-center justify-between mt-1">
                                        <span class="font-mono">{{ p.periodo }}</span>
                                        <span>{{ p.start_date ? p.start_date.substring(5) : '' }} ~ {{ p.end_date ? p.end_date.substring(5) : '' }}</span>
                                    </div>
                                </button>
                            </div>
                        </div>

                        <!-- Periodo y Monto -->
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Periodo (AñoMes) *</label>
                                <input type="text" v-model="massiveForm.periodo" required placeholder="Ej. 202601" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-mono font-bold" />
                            </div>
                            <div>
                                <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Monto por Estudiante (S/) *</label>
                                <input type="number" step="0.01" min="0.01" v-model="massiveForm.total_amount" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-bold" />
                            </div>
                        </div>

                        <!-- Concepto -->
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Concepto / Glosa *</label>
                            <input type="text" v-model="massiveForm.label" required placeholder="Ej. Inglés - Mes 1" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>

                        <!-- Fechas -->
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Fecha Emisión / Inicio</label>
                                <input type="date" v-model="massiveForm.fecha_emision" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                            </div>
                            <div>
                                <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Fecha Vencimiento / Fin</label>
                                <input type="date" v-model="massiveForm.fecha_vencimiento" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                            </div>
                        </div>
                    </div>

                    <!-- Botones -->
                    <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                        <button type="button" @click="showMassiveModal = false" class="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">Cancelar</button>
                        <button type="submit" :disabled="savingMassive" class="btn-premium-primary px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center gap-2 shadow-md">
                            <Icon v-if="savingMassive" icon="tabler:loader-2" class="w-4 h-4 animate-spin" />
                            <span>Generar Órdenes</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Modal Creación / Edición Individual -->
        <div v-if="showOrderModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div class="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-4 animate-in fade-in zoom-in-95 duration-200">
                <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
                    <h3 class="text-base font-bold text-slate-800 dark:text-white">
                        {{ isEditing ? 'Editar Orden de Pago' : 'Nueva Orden de Pago' }}
                    </h3>
                    <button @click="showOrderModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1">
                        <Icon icon="tabler:x" class="w-5 h-5" />
                    </button>
                </div>

                <div v-if="orderError" class="p-3 rounded-xl text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                    {{ orderError }}
                </div>

                <form @submit.prevent="submitOrder" class="space-y-3.5 text-xs">
                    <!-- Estudiante -->
                    <div>
                        <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Estudiante *</label>
                        <SelectSearch
                            v-model="orderForm.id_student"
                            :options="studentOptions"
                            placeholder="Buscar y seleccionar estudiante..."
                            :disabled="isEditing"
                        />
                    </div>

                    <!-- Curso -->
                    <div>
                        <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Curso de Formación</label>
                        <SelectSearch
                            v-model="orderForm.id_course"
                            :options="courseIndividualOptions"
                            placeholder="Seleccione curso (o general)..."
                        />
                    </div>

                    <!-- Periodos configurados para individual (si existen) -->
                    <div v-if="individualCoursePeriods && individualCoursePeriods.length > 0" class="p-2.5 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/50 space-y-1.5">
                        <span class="font-bold text-indigo-900 dark:text-indigo-200 text-[10px]">Auto-rellenar desde periodo configurado:</span>
                        <div class="flex flex-wrap gap-1.5">
                            <button v-for="p in individualCoursePeriods" :key="p.id" type="button" @click="applyIndividualPeriod(p)"
                                    class="px-2 py-1 bg-white dark:bg-slate-800 border rounded-lg text-[10px] hover:border-indigo-500">
                                {{ p.label }} ({{ p.periodo }})
                            </button>
                        </div>
                    </div>

                    <!-- Periodo y Monto -->
                    <div class="grid grid-cols-2 gap-3">
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Periodo *</label>
                            <input type="text" v-model="orderForm.periodo" required placeholder="Ej. 202601" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-mono font-bold" />
                        </div>
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Monto Total (S/) *</label>
                            <input type="number" step="0.01" min="0.01" v-model="orderForm.total_amount" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-bold" />
                        </div>
                    </div>

                    <!-- Concepto -->
                    <div>
                        <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Concepto / Glosa *</label>
                        <input type="text" v-model="orderForm.label" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                    </div>

                    <!-- Fechas -->
                    <div class="grid grid-cols-2 gap-3">
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Fecha Emisión</label>
                            <input type="date" v-model="orderForm.fecha_emision" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Fecha Vencimiento</label>
                            <input type="date" v-model="orderForm.fecha_vencimiento" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>
                    </div>

                    <!-- Botones -->
                    <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                        <button type="button" @click="showOrderModal = false" class="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">Cancelar</button>
                        <button type="submit" :disabled="savingOrder" class="btn-premium-primary px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center gap-2 shadow-md">
                            <Icon v-if="savingOrder" icon="tabler:loader-2" class="w-4 h-4 animate-spin" />
                            <span>{{ isEditing ? 'Guardar Cambios' : 'Crear Orden' }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Modal Modificación Masiva de Órdenes (Precio, Fecha Emisión, Fecha Vencimiento) -->
        <div v-if="showMassiveEditModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div class="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-4 animate-in fade-in zoom-in-95 duration-200">
                <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
                    <div class="flex items-center gap-2.5">
                        <div class="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">
                            <Icon icon="tabler:edit" class="w-5 h-5" />
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-slate-800 dark:text-white">
                                Modificación Masiva de Órdenes
                            </h3>
                            <p class="text-xs text-slate-400">
                                {{ massiveEditForm.all_filtered ? `Aplicando a todas las ${pagination.total} órdenes filtradas` : `Aplicando a ${selectedOrderIds.length} órdenes seleccionadas` }}
                            </p>
                        </div>
                    </div>
                    <button @click="showMassiveEditModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1">
                        <Icon icon="tabler:x" class="w-5 h-5" />
                    </button>
                </div>

                <div v-if="massiveEditResult" class="p-3.5 rounded-xl text-xs font-semibold" :class="massiveEditResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'">
                    {{ massiveEditResult.message }}
                </div>

                <div class="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 rounded-xl text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                    <Icon icon="tabler:alert-triangle" class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <span>Solo se modificarán las órdenes que <strong>no tengan pagos registrados</strong> (abono = 0). Las órdenes con pagos se mantendrán intactas.</span>
                </div>

                <form @submit.prevent="submitMassiveEdit" class="space-y-4 text-xs">
                    <!-- Opción 1: Modificar Precio / Monto -->
                    <div class="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 space-y-2.5">
                        <label class="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input type="checkbox" v-model="massiveEditForm.change_amount" class="rounded text-indigo-600 focus:ring-indigo-500" />
                            <span>Cambiar Precio / Monto Total</span>
                        </label>
                        <div v-if="massiveEditForm.change_amount" class="pl-6">
                            <label class="block text-[11px] font-semibold text-slate-500 mb-1">Nuevo Monto Total (S/)</label>
                            <input type="number" step="0.01" min="0.01" v-model="massiveEditForm.total_amount" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 font-bold" />
                        </div>
                    </div>

                    <!-- Opción 2: Modificar Fecha de Inicio / Emisión -->
                    <div class="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 space-y-2.5">
                        <label class="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input type="checkbox" v-model="massiveEditForm.change_emission_date" class="rounded text-indigo-600 focus:ring-indigo-500" />
                            <span>Cambiar Fecha de Inicio / Emisión</span>
                        </label>
                        <div v-if="massiveEditForm.change_emission_date" class="pl-6">
                            <label class="block text-[11px] font-semibold text-slate-500 mb-1">Nueva Fecha de Emisión</label>
                            <input type="date" v-model="massiveEditForm.fecha_emision" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200" />
                        </div>
                    </div>

                    <!-- Opción 3: Modificar Fecha de Fin / Vencimiento -->
                    <div class="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 space-y-2.5">
                        <label class="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input type="checkbox" v-model="massiveEditForm.change_due_date" class="rounded text-indigo-600 focus:ring-indigo-500" />
                            <span>Cambiar Fecha de Fin / Vencimiento</span>
                        </label>
                        <div v-if="massiveEditForm.change_due_date" class="pl-6">
                            <label class="block text-[11px] font-semibold text-slate-500 mb-1">Nueva Fecha de Vencimiento</label>
                            <input type="date" v-model="massiveEditForm.fecha_vencimiento" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200" />
                        </div>
                    </div>

                    <!-- Botones Footer -->
                    <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                        <button type="button" @click="showMassiveEditModal = false" class="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">Cancelar</button>
                        <button type="submit" :disabled="isUpdatingMassive" class="btn-premium-primary px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center gap-2 shadow-md">
                            <Icon v-if="isUpdatingMassive" icon="tabler:loader-2" class="w-4 h-4 animate-spin" />
                            <span>Aplicar Cambios Masivos</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>
